import { Flex, FormLabel } from '@chakra-ui/react';
import { COLOR_EDITED, Mixpanel } from 'libs/third-party/Mixpanel';
import { FocusEvent, KeyboardEvent, MouseEvent, memo, useCallback } from 'react';
import { HexColorInput } from 'react-colorful';
import { getAlpha, getColorWithAlphaStringHex, toHex } from 'utils/color';

import { ColorInputProps } from './ColorInput.types';

const hexInputTestId = 'colorpicker-hex-color-input';
const hexInputId = 'colorpicker-hex-input';
const hexLabel = 'HEX';

export const ColorInput = memo(({ color, onChange, containerProps, fontSize = 'xs' }: ColorInputProps) => {
  // The color input only accepts hex values
  const hexColor = toHex(color);

  const onChangeColor = (newColor: string) => {
    // If the color is the same, don't do anything
    if (newColor === hexColor) return;

    // the color can have alpha value but the newColor from the input won't have alpha
    // so we need to add the alpha value to the newColor
    // to check if the color is the same
    const alpha = getAlpha(color);
    if (getColorWithAlphaStringHex(newColor, alpha) === hexColor) return;

    const newColorHex = toHex(newColor);
    onChange(newColorHex);

    Mixpanel.track(COLOR_EDITED, { from: COLOR_EDITED.from.Hex });
  };

  const onBlurInput = (event: FocusEvent<HTMLInputElement>) => {
    const inputColor = event.target.value;
    onChangeColor(inputColor);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const inputColor = (event.target as HTMLInputElement).value;
    if (event.key === 'Enter') onChangeColor(inputColor);
  };

  // Highlight all text when click on hex colour input
  const onClick = useCallback((e: MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select(), []);

  return (
    <Flex textAlign='center' w='7rem' {...containerProps}>
      <FormLabel mt={1} htmlFor={hexInputId} textAlign='center' fontWeight='medium' fontSize={fontSize}>
        {hexLabel}
      </FormLabel>
      <HexColorInput
        color={hexColor}
        data-testid={hexInputTestId}
        prefixed
        id={hexInputId}
        onKeyDown={onInputKeyDown}
        onBlur={onBlurInput}
        onClick={onClick}
      />
    </Flex>
  );
});
ColorInput.displayName = 'ColorInput';
